<template>
  <div
    class="vc-section vc-card vc-card--compact vc-interface-mgmt"
    :class="{ 'vc-interface-mgmt--collapsed': !expanded, 'is-disabled': disabled }"
  >
    <div
      class="vc-interface-mgmt__head"
      role="button"
      :tabindex="disabled ? -1 : 0"
      :title="disabled ? '' : expanded ? '收起' : '展开'"
      @click="toggleExpanded"
      @keyup.enter="toggleExpanded"
    >
      <span class="vc-interface-mgmt__title">接口管理</span>
      <span class="vc-interface-mgmt__chevron">
        <Icon :type="expanded ? 'ios-arrow-up' : 'ios-arrow-down'" size="18" />
      </span>
    </div>

    <div v-show="expanded" class="vc-interface-mgmt__body">
      <div class="vc-interface-mgmt__toolbar">
        <div class="vc-interface-mgmt__mode-toggle" role="tablist" aria-label="接口列表展示模式">
          <button
            type="button"
            class="vc-interface-mgmt__mode-btn"
            :class="{ 'is-active': viewMode === VIEW_MODE.JSON }"
            @click.stop="viewMode = VIEW_MODE.JSON"
          >
            JSON 模式
          </button>
          <button
            type="button"
            class="vc-interface-mgmt__mode-btn"
            :class="{ 'is-active': viewMode === VIEW_MODE.FORM }"
            @click.stop="viewMode = VIEW_MODE.FORM"
          >
            表单模式
          </button>
        </div>
        <Button type="text" class="vc-interface-mgmt__add-btn" :disabled="disabled" @click.stop="openAddModal">
          + 调用接口
        </Button>
      </div>

      <div v-if="viewMode === VIEW_MODE.JSON" class="vc-interface-mgmt__table">
        <div class="vc-interface-mgmt__table-head vc-interface-mgmt__table-head--json">
          <span>名称</span>
          <span>接口 JSON</span>
          <span>操作</span>
        </div>
        <div v-if="!currentExternalApis.length" class="vc-interface-mgmt__empty">暂无接口配置</div>
        <div
          v-for="(row, index) in currentExternalApis"
          :key="row.id || 'api-json-' + index"
          class="vc-interface-mgmt__table-row vc-interface-mgmt__table-row--json"
        >
          <span class="vc-interface-mgmt__cell-name">{{ displayName(row) }}</span>
          <Tooltip
            :disabled="!isJsonTruncated(row)"
            :content="jsonCompact(row)"
            placement="top"
            :max-width="520"
            transfer
          >
            <span class="vc-interface-mgmt__cell-json">{{ jsonCompactPreview(row) }}</span>
          </Tooltip>
          <span class="vc-interface-mgmt__cell-actions">
            <a class="vc-interface-mgmt__link" :class="{ 'is-disabled': disabled }" @click.stop="openEditModal(index)">编辑</a>
            <Poptip
              :value="exclusiveConfirmKey === 'api-json-' + index"
              confirm
              placement="top-end"
              transfer
              title="确定删除该接口吗？"
              ok-text="确定"
              cancel-text="取消"
              @click.native.stop
              @input="onExclusiveConfirmInput('api-json-' + index, $event)"
              @on-ok="removeRow(index)"
            >
              <a class="vc-interface-mgmt__link vc-interface-mgmt__link--danger" :class="{ 'is-disabled': disabled }">删除</a>
            </Poptip>
          </span>
        </div>
      </div>

      <div v-else class="vc-interface-mgmt__table">
        <div class="vc-interface-mgmt__table-head vc-interface-mgmt__table-head--form">
          <span>名称</span>
          <span>方法</span>
          <span>接口地址</span>
          <span>输入参数</span>
          <span>Header参数</span>
          <span>操作</span>
        </div>
        <div v-if="!currentExternalApis.length" class="vc-interface-mgmt__empty">暂无接口配置</div>
        <div
          v-for="(row, index) in currentExternalApis"
          :key="row.id || 'api-form-' + index"
          class="vc-interface-mgmt__table-row vc-interface-mgmt__table-row--form"
        >
          <span class="vc-interface-mgmt__cell-name">{{ displayName(row) }}</span>
          <span class="vc-interface-mgmt__cell-method">
            <span class="vc-interface-mgmt__method-badge">{{ methodLabel(row) }}</span>
          </span>
          <span class="vc-interface-mgmt__cell-url" :title="urlLabel(row)">{{ urlLabel(row) }}</span>
          <span class="vc-interface-mgmt__cell-params" :title="inputParamsLabel(row)">
            {{ inputParamsLabel(row) }}
          </span>
          <span class="vc-interface-mgmt__cell-headers" :title="headersLabel(row)">
            {{ headersLabel(row) }}
          </span>
          <span class="vc-interface-mgmt__cell-actions">
            <a class="vc-interface-mgmt__link" :class="{ 'is-disabled': disabled }" @click.stop="openEditModal(index)">编辑</a>
            <Poptip
              :value="exclusiveConfirmKey === 'api-form-' + index"
              confirm
              placement="top-end"
              transfer
              title="确定删除该接口吗？"
              ok-text="确定"
              cancel-text="取消"
              @click.native.stop
              @input="onExclusiveConfirmInput('api-form-' + index, $event)"
              @on-ok="removeRow(index)"
            >
              <a class="vc-interface-mgmt__link vc-interface-mgmt__link--danger" :class="{ 'is-disabled': disabled }">删除</a>
            </Poptip>
          </span>
        </div>
      </div>
    </div>

    <ExternalApiModal
      v-model="modalVisible"
      :edit-index="editingIndex"
      :initial-data="editingItem"
      :edit-mode="modalEditMode"
      @submit="onModalSubmit"
    />
  </div>
</template>

<script>
import { EXTERNAL_API_VIEW_MODE } from '../../constants/externalApi'
import {
  createDefaultExternalApiItem,
  formatHeadersForTable,
  formatInputParamsForTable,
  getExternalApiDisplayName,
  normalizeExternalApiItem,
  stringifyInterfaceJsonCompact,
  truncateDisplayText,
} from '../../utils/externalApiHelpers'
import ExternalApiModal from './ExternalApiModal.vue'
import { exclusiveConfirmMixin } from '../../utils/exclusiveConfirm'

export default {
  name: 'LlmInterfaceManagementSection',
  mixins: [exclusiveConfirmMixin],
  components: { ExternalApiModal },
  props: {
    externalApisJson: {
      type: Array,
      required: true,
    },
    externalApisForm: {
      type: Array,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      VIEW_MODE: EXTERNAL_API_VIEW_MODE,
      JSON_PREVIEW_MAX_LENGTH: 80,
      expanded: false,
      viewMode: EXTERNAL_API_VIEW_MODE.JSON,
      modalVisible: false,
      editingIndex: -1,
      editingItem: null,
      modalEditMode: EXTERNAL_API_VIEW_MODE.JSON,
    }
  },
  computed: {
    currentExternalApis() {
      return this.viewMode === this.VIEW_MODE.FORM ? this.externalApisForm : this.externalApisJson
    },
    hasAnyExternalApiData() {
      return this.externalApisJson.length > 0 || this.externalApisForm.length > 0
    },
  },
  watch: {
    hasAnyExternalApiData: {
      immediate: true,
      handler(hasData) {
        if (this.disabled) return
        if (hasData) {
          this.expanded = true
        }
      },
    },
    'currentExternalApis.length': {
      handler(len, oldLen) {
        if (this.disabled) return
        if (len > 0 && (oldLen === undefined || oldLen === 0)) {
          this.expanded = true
        }
      },
    },
    disabled: {
      immediate: true,
      handler(locked) {
        if (locked) this.expanded = false
      },
    },
  },
  methods: {
    toggleExpanded() {
      if (this.disabled) return
      this.expanded = !this.expanded
    },
    displayName(row) {
      return getExternalApiDisplayName(row)
    },
    jsonCompact(row) {
      return stringifyInterfaceJsonCompact(row)
    },
    jsonCompactPreview(row) {
      return truncateDisplayText(this.jsonCompact(row), this.JSON_PREVIEW_MAX_LENGTH)
    },
    isJsonTruncated(row) {
      const full = this.jsonCompact(row)
      return full.length > this.JSON_PREVIEW_MAX_LENGTH
    },
    methodLabel(row) {
      const item = normalizeExternalApiItem(row)
      return item.method || 'POST'
    },
    urlLabel(row) {
      const item = normalizeExternalApiItem(row)
      if (item.url) return item.url
      const json = item.interface_json
      return json && json.url ? String(json.url) : '-'
    },
    inputParamsLabel(row) {
      return formatInputParamsForTable(row)
    },
    headersLabel(row) {
      return formatHeadersForTable(row)
    },
    openAddModal() {
      if (this.disabled) return
      this.editingIndex = -1
      this.editingItem = createDefaultExternalApiItem(this.viewMode)
      this.modalEditMode = this.viewMode
      this.modalVisible = true
    },
    openEditModal(index) {
      if (this.disabled) return
      const row = this.currentExternalApis[index]
      if (!row) return
      this.editingIndex = index
      this.editingItem = normalizeExternalApiItem(row)
      this.modalEditMode = this.viewMode
      this.modalVisible = true
    },
    onModalSubmit(item) {
      const normalized = normalizeExternalApiItem({
        ...item,
        mode: this.viewMode,
      })
      const list = this.currentExternalApis
      if (this.editingIndex >= 0) {
        this.$set(list, this.editingIndex, normalized)
      } else {
        list.push(normalized)
      }
    },
    removeRow(index) {
      if (this.disabled) return
      this.closeExclusiveConfirm(
        this.viewMode === this.VIEW_MODE.FORM ? 'api-form-' + index : 'api-json-' + index,
      )
      this.currentExternalApis.splice(index, 1)
    },
  },
}
</script>

<style lang="scss" scoped>
@import '../voice-call/shared.scss';

.vc-section {
  margin-bottom: 16px;
}

.vc-card {
  background: #fff;
  border: 1px solid #e8eaec;
  border-radius: 8px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  padding: 20px;
}

.vc-card--compact {
  padding-bottom: 16px;
}

.vc-interface-mgmt--collapsed {
  padding-bottom: 20px;

  .vc-interface-mgmt__head {
    margin-bottom: 0;
  }
}

.vc-interface-mgmt.is-disabled {
  .vc-interface-mgmt__title,
  .vc-interface-mgmt__chevron {
    color: #bfbfbf;
  }

  .vc-interface-mgmt__head {
    cursor: default;
    pointer-events: none;
  }
}

.vc-interface-mgmt__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  min-height: 24px;
  cursor: pointer;
  user-select: none;
}

.vc-interface-mgmt__title {
  font-size: 15px;
  font-weight: 600;
  color: #262626;
  line-height: 24px;
}

.vc-interface-mgmt__chevron {
  display: inline-flex;
  align-items: center;
  color: #3e3e3e;
  flex-shrink: 0;
}

.vc-interface-mgmt__toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}

.vc-interface-mgmt__mode-toggle {
  display: inline-flex;
  align-items: stretch;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  overflow: hidden;
  background: #f5f7fa;
}

.vc-interface-mgmt__mode-btn {
  border: none;
  background: transparent;
  color: #909399;
  font-size: 13px;
  line-height: 1.4;
  padding: 6px 14px;
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease;

  &.is-active {
    background: #5668f0;
    color: #fff;
  }
}

.vc-interface-mgmt__add-btn {
  flex-shrink: 0;
  color: #5668f0 !important;
  padding: 2px 8px !important;
  height: auto !important;

  &:hover {
    color: #3f51e8 !important;
    background: #f5f7ff !important;
  }

  &[disabled],
  &.ivu-btn-disabled {
    color: #c5c8ce !important;
    background: transparent !important;
    cursor: not-allowed !important;
  }
}

.vc-interface-mgmt__table {
  border: 1px solid #e8eaec;
  border-radius: 4px;
}

.vc-interface-mgmt__table-head,
.vc-interface-mgmt__table-row {
  display: grid;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  font-size: 13px;
  line-height: 1.5;
  text-align: left;
  justify-items: start;
}

.vc-interface-mgmt__table-head {
  background: #fafafa;
  color: #8c8c8c;
  font-weight: 500;
  border-bottom: 1px solid #e8eaec;
}

.vc-interface-mgmt__table-head--json,
.vc-interface-mgmt__table-row--json {
  grid-template-columns: 240px minmax(0, 1fr) 96px;
}

.vc-interface-mgmt__table-head--form,
.vc-interface-mgmt__table-row--form {
  grid-template-columns: 240px 72px minmax(0, 1.2fr) minmax(0, 1fr) minmax(0, 0.8fr) 88px;
}

.vc-interface-mgmt__table-row {
  color: #515a6e;
  border-bottom: 1px solid #f0f0f0;

  &:last-child {
    border-bottom: none;
  }
}

.vc-interface-mgmt__cell-name {
  min-width: 0;
  color: #262626;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.vc-interface-mgmt__cell-json,
.vc-interface-mgmt__cell-url,
.vc-interface-mgmt__cell-params,
.vc-interface-mgmt__cell-headers {
  display: block;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-family: Menlo, Monaco, Consolas, 'Courier New', monospace;
  font-size: 12px;
}

.vc-interface-mgmt__cell-json {
  cursor: default;
}

.vc-interface-mgmt__method-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 44px;
  height: 22px;
  padding: 0 8px;
  border-radius: 4px;
  background: #e6f4ff;
  color: #1890ff;
  font-size: 12px;
  font-weight: 600;
  font-family: inherit;
}

.vc-interface-mgmt__cell-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  justify-content: flex-start;
}

.vc-interface-mgmt__link {
  color: #5668f0;
  cursor: pointer;
  text-decoration: none;

  &:hover {
    color: #3f51e8;
  }

  &.is-disabled {
    color: #c5c8ce;
    cursor: not-allowed;
    pointer-events: none;
  }
}

.vc-interface-mgmt__link--danger {
  color: #ed4014;

  &:hover {
    color: #c41d1d;
  }
}

.vc-interface-mgmt__empty {
  padding: 24px 12px;
  text-align: center;
  color: #bfbfbf;
  font-size: 13px;
}
</style>
